import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { AccountingEventType, DocumentState } from '@received/pricing-model';
import { DateRangeFilters, InvoiceTable, Sidebars, SummaryBar } from '../../../../../components';
import { SidebarType } from '../../../../../components/Sidebars/Sidebars.utils';
import { fullFiltersSelectOptions, generalTimePeriodInitialState } from '../../../../../components/Filters/DateRangeFilters.utils';
import { invoicesTabTableHeader } from '../../../../../components/_customerTabs/InvoicesTab/InvoicesTab.utils';
import { DatesData, Sort, SortOrder } from '../../../../../types/generalTypes';
import { InvoiceSummary, InvoiceView } from '../../../../../types/invoiceTypes';
import { InvoiceResponse } from '../../../../_Billing/Invoices/AllInvoices/AllInvoices.utils';
import { getSortingOrderList, getSummaryBlocksData, getSummaryData } from '../../../../../utils/InvoiceUtils';
import { PathsConfig } from '../../../../../services/httpService/configPaths';
import { httpService } from '../../../../../services/httpService/httpService';
import { successErrorMassageOptions } from '../../../../../components/SuccessErrorModal/SuccessErrorModal.utils';
import { setOpenSuccessErrorModal } from '../../../../../storeSlices/errorSuccessSlice';
import styles from './ContractInvoices.module.scss';

const defaultNumberOfItems = '20';

interface ContractInvoicesProps {
	contractId: string;
	customerId?: string | null;
}

export const ContractInvoices = ({ contractId, customerId }: ContractInvoicesProps) => {
	const { t } = useTranslation('translation');
	const dispatch = useDispatch();

	const [invoicesList, setInvoicesList] = useState<InvoiceView[]>([]);
	const [totalCount, setTotalCount] = useState(0);
	const [pageNumber, setPageNumber] = useState(0);
	const [numberOfItems, setNumberOfItems] = useState(defaultNumberOfItems);
	const [sort, setSort] = useState<Sort>({ orderBy: ['issueDate', 'draftNumber'], order: SortOrder.ASC });
	const [invoicesSummary, setInvoiceSummary] = useState<InvoiceSummary>();
	const [timePeriod, setTimePeriod] = useState<DatesData>(generalTimePeriodInitialState);
	const [openSideBarById, setOpenSideBarById] = useState<{ sideBarType: SidebarType; id: string }>();

	useEffect(() => {
		contractId && refreshData();
	}, [contractId, numberOfItems, pageNumber, sort, timePeriod]);

	const getInvoiceData = async () => {
		try {
			const res: InvoiceResponse = (
				await httpService({
					dispatch,
					path: PathsConfig.getInvoicesViewData,
					params: {
						contractId,
						pageSize: parseInt(numberOfItems),
						pageNumber: pageNumber,
						orderBy: sort.orderBy,
						order: sort.order,
						excludedStates: [DocumentState.CONTRACT_DRAFT],
						fromIssueDate: timePeriod.from,
						toIssueDate: timePeriod.to,
					},
				})
			).data;
			setTotalCount(res?.count);
			setInvoicesList(res?.data);
		} catch (error) {
			dispatch(setOpenSuccessErrorModal({ responseError: error, successErrorMassage: successErrorMassageOptions.ERROR.ALL_INVOICE_DATA }));
		}
	};

	const refreshData = async () => {
		await getInvoiceData();
		const summary = await getSummaryData(
			dispatch,
			timePeriod.from,
			timePeriod.to,
			customerId ? [customerId] : undefined,
			[AccountingEventType.INVOICE, AccountingEventType.DEBIT],
			undefined,
			contractId,
		);
		setInvoiceSummary(summary);
	};

	return (
		<div className={styles.mainContainer}>
			<div className={styles.header}>
				<span className={styles.title}>{t('invoices')}</span>
				<DateRangeFilters data={fullFiltersSelectOptions} defaultSelectValue={timePeriod.title} callback={setTimePeriod} />
			</div>

			<SummaryBar summary={getSummaryBlocksData(t, invoicesSummary)} blockClassName={styles.blockClassName} />

			<InvoiceTable
				refreshData={refreshData}
				tableProps={{
					sort: sort,
					numberOfItems: numberOfItems,
					header: invoicesTabTableHeader(refreshData),
					rows: invoicesList,
					className: styles.tableContainer,
					numberOfPages: Math.ceil(totalCount / +numberOfItems),
					totalNumberOfItems: totalCount,
					onUpdateNumberOfItems: setNumberOfItems,
					onUpdatePageNumber: setPageNumber,
					onRowPress: (data) => setOpenSideBarById({ sideBarType: SidebarType.INVOICE, id: data.invoiceId }),
					onFlipOrder: (accessor) =>
						setSort({ orderBy: getSortingOrderList(accessor), order: sort.order === SortOrder.ASC ? SortOrder.DESC : SortOrder.ASC }),
				}}
			/>

			<Sidebars openSideBarById={openSideBarById} onCloseSideBars={() => setOpenSideBarById(undefined)} onDataChange={refreshData} />
		</div>
	);
};
